import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

const MODEL_TASKS_QUERY_KEY = "modelTasks";

const sameTask = (task, taskId) => String(task?.id) === String(taskId);

// Task detail endpoints return the same display strings the list columns render
// (roles, zones, stages, assets), but they omit fields the caller did not touch.
const definedEntries = (changes) =>
	Object.fromEntries(
		Object.entries(changes ?? {}).filter(([, value]) => value !== undefined)
	);

// Returning undefined tells react-query to skip the entry entirely. Returning
// the unchanged object instead would still register a manual success, bumping
// dataUpdatedAt and clearing isInvalidated on a query we never touched.
const patchCachedPages = (data, taskId, changes) => {
	if (!data?.pages) return undefined;
	let patched = false;
	const pages = data.pages.map((page) => {
		if (!page?.items?.some((task) => sameTask(task, taskId))) return page;
		patched = true;
		return {
			...page,
			items: page.items.map((task) =>
				sameTask(task, taskId) ? { ...task, ...changes } : task
			),
		};
	});
	return patched ? { ...data, pages } : undefined;
};

/**
 * Keeps the cached task list in step with edits made in an expanded row.
 *
 * The list query is the single source of truth for what a row displays, so a
 * detail edit has to land here rather than being painted onto the DOM.
 */
const useModelTaskListSync = (modelId) => {
	const queryClient = useQueryClient();
	const filters = useMemo(
		() => ({ queryKey: [MODEL_TASKS_QUERY_KEY, modelId] }),
		[modelId]
	);

	const patchTask = useCallback(
		(taskId, changes) => {
			const applied = definedEntries(changes);
			if (Object.keys(applied).length === 0) return;
			queryClient.setQueriesData(filters, (data) =>
				patchCachedPages(data, taskId, applied)
			);
		},
		[filters, queryClient]
	);

	const invalidate = useCallback(
		() => queryClient.invalidateQueries(filters),
		[filters, queryClient]
	);

	return { patchTask, invalidate };
};

export { MODEL_TASKS_QUERY_KEY };
export default useModelTaskListSync;
